import React, { useEffect, useState } from 'react';
import {
  Box,
  MenuItem,
  Button,
  Checkbox,
  Typography,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Paper,
  CircularProgress,
  Grid,
  Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, DialogContentText
} from '@mui/material';
import { ApiServices } from '../../utils/ApiService';
import { useSnackbar } from 'notistack';
import SaveIcon from "@mui/icons-material/Save";

const fieldStyle = { marginBottom: '20px' };

const registerFileData = {
  fileName: '',
  propertyOwnerName: '',
  bankName: '',
  bankBranch: '',
  bankProduct: '',
  bankEmail: '',
  applicationId: '',
  titleDeedNo: '',
  surveyNo: '',
  extent: '',
  loginType: '',
  yaazhBranch: '' // Added field
};

const arrDocumentCategory = [
  'Title deed',
  'Mother Document',
  'Sale Agreement',
  'Revenue record',
  'EC'
];

const arrLoginType = ['Normal', 'BT/ Takeover', 'Purchase', 'Topup'];

const CreateFilePage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState({ ...registerFileData });
  const [filesByCategory, setFilesByCategory] = useState({});
  const [errors, setErrors] = useState({});
  const [createdBy, setCreatedBy] = useState(null);
  const [arrBankCategory, setArrBankCategory] = useState([]);
  const [arrBankBranchCategory, setArrBankBranchCategory] = useState([]);
  const [arrBankProductCategory, setArrBankProductCategory] = useState([]);
  const [arrBranches, setArrBranches] = useState([]); // New: Yaazh branches
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [sameAsApplicant, setSameAsApplicant] = useState(false);
  const [manualOwnerName, setManualOwnerName] = useState("");
  const handleSameAsApplicant = (e) => {
    const checked = e.target.checked;
    setSameAsApplicant(checked);

    if (checked) {
      setData(prev => ({
        ...prev,
        propertyOwnerName: prev.fileName
      }));
    }
  };

  // Cancel changes
  const handleCancel = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const file_tab = user.file_tab || [];
    const branch_tab = user.branch_tab || [];

    setCreatedBy(Number(user?.id));
    setArrBankCategory(file_tab?.map(obj => obj.bank_name));
    setArrBranches(branch_tab?.map(b => b.branch?.trim())); // Remove empty ones
    setLoading(false);
  }, []);

  const handleCategoryFileChange = (e, category) => {
    const selectedFiles = e.target.files;

    const oversizedFiles = Array.from(selectedFiles)?.filter(file => {
      const sizeInMB = file.size / 1024 / 1024;
      return sizeInMB > 10;
    });

    if (oversizedFiles.length > 0) {
      enqueueSnackbar(
        `Each file for "${category}" must be under 10 MB. Please compress or reduce the size.`,
        { variant: 'warning' }
      );
      return;
    }

    const nonPdfFiles = Array.from(selectedFiles).filter(
      file => file.type !== 'application/pdf'
    );

    if (nonPdfFiles.length > 0) {
      enqueueSnackbar(`Only PDF files are allowed for "${category}".`, {
        variant: 'warning'
      });
      return;
    }

    setFilesByCategory(prev => ({
      ...prev,
      [category]: selectedFiles,
    }));

    if (selectedFiles.length > 0) {
      setErrors(prev => ({ ...prev, [`files_${category}`]: '' }));
    }
  };

  const handleChange = (e, key) => {
    const value = e.target.value;
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const file_tab = user.file_tab || [];

    if (key === 'bankName') {
      const objBank = file_tab.find(obj => obj.bank_name === value);
      const arrBranch = objBank?.branches?.map(b => b.branch_name) || [];
      const arrProducts = objBank?.products || [];

      setArrBankBranchCategory(arrBranch);
      setArrBankProductCategory(arrProducts);
      setData(prev => ({
        ...prev,
        [key]: value,
        bankBranch: '',
        bankEmail: ''
      }));
    } else if (key === 'bankBranch') {
      const objBank = file_tab.find(obj => obj.bank_name === data.bankName);
      const objBranch = objBank?.branches?.find(b => b.branch_name === value);
      setData(prev => ({
        ...prev,
        [key]: value,
        bankEmail: objBranch?.email || ''
      }));
    } else {
      setData(prev => {
        const updated = { ...prev, [key]: value };

        if (key === 'fileName' && sameAsApplicant) {
          updated.propertyOwnerName = value;
        }

        return updated;
      });
    }

    if (value.trim()) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const validateForm = () => {
    const requiredFields = Object.keys(registerFileData);
    const newErrors = {};

    requiredFields.forEach(field => {
      if (!data[field]?.trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar('Please fix the form errors before submitting.', {
        variant: 'warning'
      });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      Object.entries(filesByCategory).forEach(([category, fileList]) => {
        Array.from(fileList).forEach(file => {
          formData.append(`files_${category.replace(/\s+/g, '_')}`, file);
        });
      });

      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('createdBy', createdBy);

      const res = await ApiServices.post('file/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res?.status === 201) {
        enqueueSnackbar(res?.data?.message || 'Registered successfully', {
          variant: 'success'
        });
        setData({ ...registerFileData });
        setFilesByCategory({});
        setErrors({});
      } else if (res?.status === 409) {
        enqueueSnackbar(res?.data?.message || 'Application ID / LAN & LOS No already exists.', {
          variant: 'warning'
        });
      } else {
        enqueueSnackbar('Error', { variant: 'error' });
      }
    } catch (error) {
      const msg = error?.response?.data?.message || 'Register failed.';
      enqueueSnackbar(msg, { variant: 'warning' });
    } finally {
      setLoading(false);
    }
  };

  const IsApplicationIdExist = async e => {
        e.preventDefault();
    try {
      const res = await ApiServices.post(`file/verify/application-id`, { applicationId: data?.applicationId });
      if (typeof res?.data === 'boolean') {
        if (res?.data === true) {
          setModalOpen(true);
        } else {
          handleSubmit(e);
        }
      } else {
        enqueueSnackbar('ApplicationId verification failed.', { variant: 'error' });
      }
    } catch (error) {
        enqueueSnackbar('ApplicationId verification failed.', { variant: 'error' });
    }
  }

      // Save changes from modal
  const handleSave = (e) => {
    setModalOpen(false);
    handleSubmit(e)
  };

  const renderSelectField = (label, key, options) => (
    <FormControl fullWidth size="medium" style={fieldStyle} error={!!errors[key]}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={data[key]} onChange={e => handleChange(e, key)}>
        {options?.map((item, idx) => (
          <MenuItem value={item} key={idx}>
            {item}
          </MenuItem>
        ))}
      </Select>
      {errors[key] && (
        <Typography color="error" variant="caption">
          {errors[key]}
        </Typography>
      )}
    </FormControl>
  );

  const renderTextField = (label, key, disabled = false) => (
    <TextField
      fullWidth
      size="medium"
      variant="outlined"
      style={fieldStyle}
      label={label}
      value={data[key]}
      onChange={e => handleChange(e, key)}
      error={!!errors[key]}
      helperText={errors[key]}
      disabled={disabled}
      InputProps={{
        readOnly: disabled,
        style: disabled ? { backgroundColor: '#f5f5f5' } : {}
      }}
    />
  );

  const renderFileUploadField = (category) => (
    <Box key={category} style={fieldStyle}>
      <Typography variant="subtitle1">{category}</Typography>
      <Button variant="contained" component="label" fullWidth size="medium">
        Upload {category}
        <input
          type="file"
          hidden
          multiple
          onChange={e => handleCategoryFileChange(e, category)}
        />
      </Button>
      {errors[`files_${category}`] && (
        <Typography color="error" variant="caption">
          {errors[`files_${category}`]}
        </Typography>
      )}
      {filesByCategory[category] && filesByCategory[category].length > 0 && (
        <Box mt={1}>
          {Array.from(filesByCategory[category])?.map((file, idx) => (
            <Typography key={idx} variant="body2">
              • {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Box sx={{ width: '100%', bgcolor: '#f0f2f5', py: 5 }}>
        {loading ? (
          <Grid container justifyContent="center" alignItems="center" style={{ height: "100%" }}>
            <CircularProgress />
          </Grid>
        ) : (
          <Paper
            elevation={4}
            sx={{
              maxWidth: 600,
              mx: 'auto',
              p: 4,
              borderRadius: 3,
              background: 'white'
            }}
          >
            <Typography variant="h5" fontWeight={600} gutterBottom>
              File Creation
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Enter the required file details below.
            </Typography>

            {renderTextField('Applicant Name', 'fileName')}

            {/* Checkbox */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <FormControlLabel
                label="Same as Applicant Name"
                labelPlacement="start"
                control={
                  <Checkbox
                    checked={sameAsApplicant}
                    onChange={handleSameAsApplicant}
                  />
                }
              />
            </Box>

            {renderTextField('Property Owner Name', 'propertyOwnerName', sameAsApplicant)}
            {renderSelectField('Bank Name', 'bankName', arrBankCategory)}
            {renderSelectField('Bank Branch', 'bankBranch', arrBankBranchCategory)}
            {renderTextField('Bank Mail ID', 'bankEmail', true)}
            {renderSelectField('Report Type', 'bankProduct', arrBankProductCategory)}
            {renderTextField('Application ID / LAN & LOS No', 'applicationId')}
            {renderTextField('Title Deed No', 'titleDeedNo')}
            {renderTextField('Survey No', 'surveyNo')}
            {renderTextField('Extent', 'extent')}
            {renderSelectField('Login Type', 'loginType', arrLoginType)}

            {/* New field: Yaazh Branch */}
            {renderSelectField('Yaazh Branch', 'yaazhBranch', arrBranches)}

            {arrDocumentCategory?.map(category => renderFileUploadField(category))}

            <Box mt={3} display="flex" justifyContent="center">
              <Button
                variant="contained"
                size="large"
                sx={{
                  px: 5,
                  py: 1.5,
                  fontWeight: 'bold',
                  borderRadius: 2,
                  background: '#1976d2',
                  '&:hover': { background: '#1565c0' }
                }}
                onClick={IsApplicationIdExist}
              >
                Save File
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

       <Dialog open={modalOpen} onClose={handleCancel}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
                <>
                  <strong> An Application ID - {data?.applicationId ?? ""} already exists. 
                    Would you like to create another file using this Application ID? </strong>{" "}
                 </>
            
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            No
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
    
  );
};

export default CreateFilePage;
